import React, { useState } from 'react';

interface Feature {
  title: string;
  description: string;
  icon?: string;
  icon_url?: string;
  status?: 'completed' | 'in-progress' | 'planned';
  impact?: 'high' | 'medium' | 'low';
  tech_used?: string[];
  metrics?: {
    label: string;
    value: string;
    improvement?: string;
  };
}

interface FeatureListVisualizationProps {
  features: Feature[];
  title: string;
  isDarkMode: boolean;
  layout?: 'grid' | 'list';
}

const FeatureListVisualization: React.FC<FeatureListVisualizationProps> = ({
  features,
  title,
  isDarkMode,
  layout = 'grid'
}) => {
  const [expandedFeature, setExpandedFeature] = useState<number | null>(null);

  const getStatusColor = (status: string) => {
    const colors = {
      'completed': isDarkMode ? '#10b981' : '#059669',
      'in-progress': isDarkMode ? '#f59e0b' : '#d97706',
      'planned': isDarkMode ? '#6b7280' : '#4b5563'
    };
    return colors[status as keyof typeof colors] || colors.planned;
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      'completed': '✅',
      'in-progress': '🔄',
      'planned': '📋'
    };
    return icons[status as keyof typeof icons] || '📋';
  };

  const getImpactColor = (impact: string) => {
    const colors = {
      'high': isDarkMode ? '#ef4444' : '#dc2626',
      'medium': isDarkMode ? '#f59e0b' : '#d97706',
      'low': isDarkMode ? '#10b981' : '#059669'
    };
    return colors[impact as keyof typeof colors] || colors.medium;
  };

  const getImpactIcon = (impact: string) => {
    const icons = {
      'high': '🔥',
      'medium': '⭐',
      'low': '💡'
    };
    return icons[impact as keyof typeof icons] || '⭐';
  };

  const toggleFeature = (index: number) => {
    setExpandedFeature(expandedFeature === index ? null : index);
  };

  return (
    <div style={{
      margin: '16px 0',
      padding: '20px',
      background: isDarkMode 
        ? 'linear-gradient(145deg, rgba(17,24,39,0.8), rgba(30,41,59,0.6))'
        : 'linear-gradient(145deg, #ffffff, #f8fafc)',
      borderRadius: '16px',
      border: `1px solid ${isDarkMode ? 'rgba(148,163,184,0.1)' : 'rgba(15,23,42,0.08)'}`,
      boxShadow: isDarkMode 
        ? '0 20px 40px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.03)'
        : '0 20px 40px rgba(15,23,42,0.06), 0 0 0 1px rgba(15,23,42,0.02)',
      position: 'relative',
      zIndex: 1
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h4 style={{
          margin: 0,
          fontSize: '18px',
          fontWeight: '700',
          color: isDarkMode ? '#f1f5f9' : '#0f172a',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '20px' }}>🚀</span>
          {title}
        </h4>
        
        <div style={{
          background: isDarkMode ? 'rgba(124,58,237,0.1)' : 'rgba(124,58,237,0.05)',
          color: isDarkMode ? '#a78bfa' : '#7c3aed',
          padding: '6px 12px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '600'
        }}>
          {features.length} features
        </div>
      </div>

      <div style={{
        display: layout === 'grid' ? 'grid' : 'flex',
        ...(layout === 'grid' ? {
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '20px',
          alignItems: 'start'
        } : {
          flexDirection: 'column',
          gap: '16px'
        })
      }}>
        {features.map((feature, index) => {
          const isExpanded = expandedFeature === index;
          
          return (
            <div
              key={index}
              className="feature-card"
              style={{
                background: isDarkMode 
                  ? 'linear-gradient(145deg, rgba(30,41,59,0.6), rgba(51,65,85,0.4))'
                  : 'linear-gradient(145deg, #ffffff, #f9fafb)',
                border: `1px solid ${isDarkMode ? 'rgba(148,163,184,0.1)' : 'rgba(15,23,42,0.06)'}`,
                borderRadius: '12px',
                padding: '16px',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                minHeight: isExpanded ? 'auto' : '140px',
                height: 'fit-content',
                alignSelf: 'start'
              }}
              onClick={() => toggleFeature(index)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = isDarkMode 
                  ? '0 12px 24px rgba(0,0,0,0.15)'
                  : '0 12px 24px rgba(15,23,42,0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Status indicator */}
              {feature.status && (
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  background: `${getStatusColor(feature.status)}20`,
                  fontSize: '10px',
                  fontWeight: '600',
                  color: getStatusColor(feature.status),
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  <span>{getStatusIcon(feature.status)}</span>
                  {feature.status.replace('-', ' ')}
                </div>
              )}

              {/* Header */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                marginBottom: '12px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: isDarkMode 
                    ? 'linear-gradient(135deg, rgba(124,58,237,0.2), rgba(168,85,247,0.1))'
                    : 'linear-gradient(135deg, rgba(124,58,237,0.1), rgba(168,85,247,0.05))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  flexShrink: 0
                }}>
                  {feature.icon_url ? (
                    <img 
                      src={feature.icon_url} 
                      alt={feature.title}
                      style={{ width: '24px', height: '24px', borderRadius: '4px' }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                        if (nextElement) nextElement.style.display = 'block';
                      }}
                    />
                  ) : null}
                  <span style={{ display: feature.icon_url ? 'none' : 'block' }}>
                    {feature.icon || '🔧'}
                  </span>
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '700',
                    color: isDarkMode ? '#f1f5f9' : '#0f172a',
                    marginBottom: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {feature.title}
                    {feature.impact && (
                      <span style={{
                        fontSize: '12px',
                        color: getImpactColor(feature.impact)
                      }}>
                        {getImpactIcon(feature.impact)}
                      </span>
                    )}
                  </div>
                  
                  <div style={{
                    fontSize: '14px',
                    color: isDarkMode ? '#cbd5e1' : '#64748b',
                    lineHeight: '1.5',
                    display: '-webkit-box',
                    WebkitLineClamp: isExpanded ? 'none' : 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {feature.description}
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div style={{
                  paddingTop: '12px',
                  borderTop: `1px solid ${isDarkMode ? 'rgba(148,163,184,0.1)' : 'rgba(15,23,42,0.06)'}`,
                  animation: 'fadeIn 0.3s ease',
                  marginBottom: '40px' // Extra space to prevent overlap with expand button
                }}>
                  {/* Tech Used */}
                  {feature.tech_used && feature.tech_used.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: isDarkMode ? '#a78bfa' : '#7c3aed',
                        marginBottom: '6px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Technologies Used
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '6px',
                        flexWrap: 'wrap'
                      }}>
                        {feature.tech_used.map((tech, techIndex) => (
                          <span
                            key={techIndex}
                            style={{
                              padding: '4px 8px',
                              background: isDarkMode ? 'rgba(124,58,237,0.1)' : 'rgba(124,58,237,0.05)',
                              color: isDarkMode ? '#c4b5fd' : '#7c3aed',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: '500'
                            }}
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Metrics */}
                  {feature.metrics && (
                    <div style={{
                      background: isDarkMode ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.05)',
                      border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.15)'}`,
                      borderRadius: '8px',
                      padding: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <span style={{ fontSize: '20px' }}>📊</span>
                      <div>
                        <div style={{
                          fontSize: '12px',
                          color: isDarkMode ? '#4ade80' : '#16a34a',
                          fontWeight: '600',
                          marginBottom: '2px'
                        }}>
                          {feature.metrics.label}
                        </div>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: '700',
                          color: isDarkMode ? '#10b981' : '#059669'
                        }}>
                          {feature.metrics.value}
                          {feature.metrics.improvement && (
                            <span style={{
                              fontSize: '12px',
                              fontWeight: '500',
                              marginLeft: '6px',
                              color: isDarkMode ? '#4ade80' : '#16a34a'
                            }}>
                              ({feature.metrics.improvement})
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Expand/Collapse indicator */}
              <div style={{
                position: 'absolute',
                bottom: '12px',
                right: '12px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                color: isDarkMode ? '#94a3b8' : '#64748b',
                transition: 'transform 0.3s ease',
                zIndex: 10
              }}>
                <span style={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }}>
                  ⌄
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          /* Ensure no overlapping between feature cards */
          .feature-card {
            contain: layout;
            isolation: isolate;
          }
        `}
      </style>
    </div>
  );
};

export default FeatureListVisualization;